import { Component, EventEmitter, Input, Output, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule, LucideIconProvider, LUCIDE_ICONS, icons } from 'lucide-angular';
import { ResolvedAddress } from '../../../models/resolved-address.model';
import { parsePlaceDetailsDto } from '../../../shared/location/address-resolution.utils';
import { googleMapsLinkErrorMessage } from '../../../shared/location/google-maps-link-error.utils';
import { isShortGoogleMapsLink, isValidGoogleMapsUrl, normalizeGoogleMapsUrl } from '../../../shared/location/google-maps-url.utils';
import { GoogleMapsLinkImportService } from '../../../services/google-maps-link-import.service';

@Component({
  selector: 'app-admin-crm-lead-onboarding-modal',
  standalone: true,
  imports: [FormsModule, LucideAngularModule],
  providers: [{ provide: LUCIDE_ICONS, multi: true, useValue: new LucideIconProvider(icons) }],
  templateUrl: './admin-crm-lead-onboarding-modal.component.html',
})
export class AdminCrmLeadOnboardingModalComponent {
  private linkImport = inject(GoogleMapsLinkImportService);

  @Input({ required: true }) open = false;
  @Output() cancelled = new EventEmitter<void>();
  @Output() manual = new EventEmitter<void>();
  @Output() imported = new EventEmitter<{ resolved: ResolvedAddress; mapsUrl: string }>();

  readonly phase = signal<'choose' | 'import'>('choose');
  readonly mapsUrl = signal('');
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);

  readonly urlLooksValid = () => isValidGoogleMapsUrl(this.mapsUrl());

  readonly isShortLink = () => {
    const normalized = normalizeGoogleMapsUrl(this.mapsUrl());
    return normalized ? isShortGoogleMapsLink(normalized) : false;
  };

  readonly loadingHint = () => {
    if (this.isShortLink()) {
      return 'Resolviendo enlace corto y extrayendo datos del local.';
    }
    return 'Puede tardar unos segundos. No cierres esta ventana.';
  };

  onBackdropClick(): void {
    if (!this.loading()) this.cancelled.emit();
  }

  pickImport(): void {
    this.error.set(null);
    this.phase.set('import');
  }

  pickManual(): void {
    this.manual.emit();
    this.#reset();
  }

  backToChoose(): void {
    if (this.loading()) return;
    this.error.set(null);
    this.mapsUrl.set('');
    this.phase.set('choose');
  }

  async submitImport(): Promise<void> {
    const url = this.mapsUrl().trim();
    if (!isValidGoogleMapsUrl(url)) {
      this.error.set('Pega un enlace válido de Google Maps (maps.app.goo.gl o google.com/maps).');
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    try {
      const response = await this.linkImport.importSpaceFromGoogle(url);
      const resolved = parsePlaceDetailsDto(response.place, '');
      if (!resolved) {
        this.error.set('La ubicación del enlace está incompleta. Prueba otro enlace.');
        return;
      }
      const normalizedUrl = normalizeGoogleMapsUrl(url) ?? url;
      this.imported.emit({ resolved, mapsUrl: normalizedUrl });
      this.#reset();
    } catch (err) {
      this.error.set(googleMapsLinkErrorMessage(err));
      console.error('[CrmLeadOnboarding] Maps link import failed:', err);
    } finally {
      this.loading.set(false);
    }
  }

  #reset(): void {
    this.phase.set('choose');
    this.mapsUrl.set('');
    this.error.set(null);
    this.loading.set(false);
  }
}
